import React from 'react'
import {
  StatusChipV4,
  StatusChipBg,
  StatusChipText,
} from '@labrav/react-components'
import { ResellerAgreementStatus } from '../../../types/flyout'
import { getStatusAndLabelForResale } from '../../utils/getStatusAndLabelForResale'

type StatusChipProp = {
  variant?: 'default' | 'error' | 'success' | 'info' | 'warning'
  bgColor: StatusChipBg
  textColor?: StatusChipText
  borderColor?: StatusChipBg
  label: string
}

export type ResaleAuthorizationStatusProps = {
  status: ResellerAgreementStatus | string
  draft?: boolean
}

const statusMap: Record<string, Omit<StatusChipProp, 'label'>> = {
  in_progress: {
    variant: 'default',
    bgColor: StatusChipBg.GRAY,
    textColor: StatusChipText.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
  active: {
    variant: 'default',
    bgColor: StatusChipBg.GREEN,
    textColor: StatusChipText.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  expired: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  completed: {
    variant: 'default',
    textColor: StatusChipText.LIGHTBLUE,
    bgColor: StatusChipBg.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  restricted: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  deactivation_in_progress: {
    variant: 'default',
    bgColor: StatusChipBg.YELLOW,
    textColor: StatusChipText.YELLOW,
    borderColor: StatusChipBg.YELLOW,
  },
  deactivated: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  failed: {
    variant: 'error',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  draft: {
    variant: 'default',
    textColor: StatusChipText.GRAY,
    bgColor: StatusChipBg.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
  pending: {
    variant: 'default',
    bgColor: StatusChipBg.YELLOW,
    textColor: StatusChipText.YELLOW,
    borderColor: StatusChipBg.YELLOW,
  },
  channel_offer_accepted: {
    variant: 'default',
    textColor: StatusChipText.LIGHTBLUE,
    bgColor: StatusChipBg.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },

  OFFER_IN_DRAFT: {
    variant: 'default',
    textColor: StatusChipText.GRAY,
    bgColor: StatusChipBg.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
  OFFER_CREATION_IN_PROGRESS: {
    variant: 'default',
    textColor: StatusChipText.LIGHTBLUE,
    bgColor: StatusChipBg.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  OFFER_CREATION_FAILED: {
    variant: 'error',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  OFFER_SENT_RESELLER: {
    variant: 'default',
    textColor: StatusChipText.LIGHTBLUE,
    bgColor: StatusChipBg.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  OFFER_VIEWED_RESELLER: {
    variant: 'default',
    textColor: StatusChipText.LIGHTBLUE,
    bgColor: StatusChipBg.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  OFFER_EXTENDED_TO_BUYER: {
    variant: 'default',
    textColor: StatusChipText.LIGHTBLUE,
    bgColor: StatusChipBg.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  OFFER_TERMS_ACCEPTED_BUYER: {
    variant: 'default',
    bgColor: StatusChipBg.YELLOW,
    textColor: StatusChipText.YELLOW,
    borderColor: StatusChipBg.YELLOW,
  },
  OFFER_PURCHASE_COMPLETE: {
    variant: 'default',
    textColor: StatusChipText.GRAY,
    bgColor: StatusChipBg.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
  OFFER_ACCEPTED: {
    variant: 'default',
    bgColor: StatusChipBg.GREEN,
    textColor: StatusChipText.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  OFFER_EXPIRED: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  OFFER_RESTRICTION_IN_PROGRESS: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  OFFER_RESTRICTED: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
}

export const ResaleAuthorizationStatus: React.FC<
  ResaleAuthorizationStatusProps
> = ({ status, draft }) => {
  const statusString =
    typeof status === 'string' ? status : (status as ResellerAgreementStatus)
  const statusConfig = statusMap[statusString]

  if (!statusConfig) {
    return null
  }

  const statusDetails = getStatusAndLabelForResale(
    status as ResellerAgreementStatus,
    draft
  )

  return (
    <StatusChipV4
      {...statusDetails}
      variant={statusConfig.variant}
      bgColor={statusConfig.bgColor}
      textColor={statusConfig.textColor ?? statusDetails.textColor}
      borderColor={statusConfig.borderColor}
    />
  )
}
